import React from 'react';
import { Plus, X } from 'lucide-react';
import ReelImage from './ReelImage';

const ReelModal = React.memo(({ reel, onClose, onAddToCompare, onRemoveFromCompare, compareList = [] }) => {
  if (!reel) return null;

  // 이미 비교 목록에 있는 크기인지 확인하는 함수
  const isInCompareList = (reelId, sizeName) => {
    return compareList.some(item => item.reelId === reelId && item.sizeName === sizeName);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button 
          className="modal-close-btn"
          onClick={onClose}
        >
          <X className="h-3 w-3" color="#6c757d" />
        </button>
        
        <div className="reel-detail">
          <div className="reel-main">
            <ReelImage 
              filename={reel.filename}
              alt={reel.name}
              className="reel-detail-image"
              onError={() => {
                console.warn('Failed to load image:', reel.filename);
              }}
            />
            <h2>{reel.name}</h2>
            <div className="reel-basic-info">
              <p><strong>제조사:</strong> {reel.manufacturer}</p>
              <p><strong>타입:</strong> {reel.reel_type}</p>
              {reel.note && <p><strong>비고:</strong> {reel.note}</p>}
            </div>
          </div>
          
          <div className="reel-sizes">
            <h3>크기별 상세 정보</h3>
            {reel.sizes && reel.sizes.length > 0 ? (
              <div className="size-list">
                {reel.sizes.map((size, index) => (
                  <div key={index} className="size-item">
                    <div className="size-header-inline">
                      <h4>{size.name}</h4>
                      <button 
                        className={`compare-add-btn ${isInCompareList(reel.id, size.name) ? 'added' : ''}`}
                        onClick={() => {
                          if (isInCompareList(reel.id, size.name)) {
                            onRemoveFromCompare(reel.id, size.name);
                          } else {
                            onAddToCompare(reel, size);
                          }
                        }}
                        title={isInCompareList(reel.id, size.name) ? '비교에서 제거' : '비교에 추가'}
                      >
                        {isInCompareList(reel.id, size.name) ? <X className="w-3 h-3" color="white" /> : <Plus className="w-3 h-3" color="white" />}
                      </button>
                    </div>
                    <div className="size-specs">
                      {size.size && (
                        <div className="spec-group">
                          <p><strong>크기:</strong> {size.size}</p>
                        </div>
                      )}
                      <div className="spec-group">
                        <p><strong>해수 저항:</strong> {size.issea ? '💧' : '❌'}</p>
                      </div>
                      {size.test_value && (
                        <div className="spec-group">
                          <p><strong>테스트:</strong> {size.test_value}</p>
                          {(size.test_value_mod1 || size.test_value_mod2) && (
                            <div className="mod-values">
                              {size.test_value_mod1 && <span className="mod-value">개조1: {size.test_value_mod1}</span>}
                              {size.test_value_mod2 && <span className="mod-value">개조2: {size.test_value_mod2}</span>}
                            </div>
                          )}
                        </div>
                      )}
                      {size.ratio_value && (
                        <div className="spec-group">
                          <p><strong>기어비:</strong> {size.ratio_value}</p>
                          {(size.ratio_value_mod1 || size.ratio_value_mod2) && (
                            <div className="mod-values">
                              {size.ratio_value_mod1 && <span className="mod-value">개조1: {size.ratio_value_mod1}</span>}
                              {size.ratio_value_mod2 && <span className="mod-value">개조2: {size.ratio_value_mod2}</span>}
                            </div>
                          )}
                        </div>
                      )}
                      {size.capacity_value && (
                        <div className="spec-group">
                          <p><strong>권사량:</strong> {size.capacity_value}</p>
                          {(size.capacity_value_mod1 || size.capacity_value_mod2) && (
                            <div className="mod-values">
                              {size.capacity_value_mod1 && <span className="mod-value">개조1: {size.capacity_value_mod1}</span>}
                              {size.capacity_value_mod2 && <span className="mod-value">개조2: {size.capacity_value_mod2}</span>}
                            </div>
                          )}
                        </div>
                      )}
                      {size.speed_value && (
                        <div className="spec-group">
                          <p><strong>감는 속도:</strong> {size.speed_value}</p>
                          {(size.speed_value_mod1 || size.speed_value_mod2) && (
                            <div className="mod-values">
                              {size.speed_value_mod1 && <span className="mod-value">개조1: {size.speed_value_mod1}</span>}
                              {size.speed_value_mod2 && <span className="mod-value">개조2: {size.speed_value_mod2}</span>}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {size.drag_max_value && (
                        <div className="spec-group">
                          <p><strong>최대 저항:</strong> {size.drag_max_value}</p>
                          {(size.drag_max_value_mod1 || size.drag_max_value_mod2) && (
                            <div className="mod-values">
                              {size.drag_max_value_mod1 && <span className="mod-value">개조1: {size.drag_max_value_mod1}</span>}
                              {size.drag_max_value_mod2 && <span className="mod-value">개조2: {size.drag_max_value_mod2}</span>}
                            </div>
                          )}
                        </div>
                      )}
                      {size.engine_power && (
                        <div className="spec-group">
                          <p><strong>엔진 파워:</strong> {size.engine_power}</p>
                        </div>
                      )}
                      {size.internal_load_value && (
                        <div className="spec-group">
                          <p><strong>내부 하중:</strong> {size.internal_load_value}</p>
                          {(size.internal_load_value_mod1 || size.internal_load_value_mod2) && (
                            <div className="mod-values">
                              {size.internal_load_value_mod1 && <span className="mod-value">개조1: {size.internal_load_value_mod1}</span>}
                              {size.internal_load_value_mod2 && <span className="mod-value">개조2: {size.internal_load_value_mod2}</span>}
                            </div>
                          )}
                        </div>
                      )}
                      {size.req_levels && (
                        <div className="spec-group">
                          <p><strong>필요 레벨:</strong> {size.req_levels}</p>
                        </div>
                      )}
                      {size.price_silver && (
                        <div className="spec-group">
                          <p><strong>가격 (실버):</strong> {size.price_silver.toLocaleString()}</p>
                        </div>
                      )}
                      {size.price_gold && (
                        <div className="spec-group">
                          <p><strong>가격 (골드):</strong> {size.price_gold}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>크기별 정보가 없습니다.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default ReelModal;
